import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";

import { InfinityHearts } from "../components/InfinityHearts";
import { useT } from "../i18n";
import { tierOf, type ProfileTier } from "./chromePlan";
import { pairAlignmentScore } from "./engine";
import { scoreBand, scoreInk } from "./weights";
import type { MatchProfile } from "./types";

/**
 * The reveal — rebuilt 2026-09-20-D (Masco: "dark like FIFA, then the cards
 * presented one by one till the four make up almost the whole screen, then
 * you press and they fall back to the menu view").
 *
 * dark → present → hold → fall → exit
 *
 * `hold` has NO timer. The old machine dismissed itself on a setTimeout, so
 * the four were gone before anyone had looked at a face. It now waits for the
 * press, and only then do the cards shrink into their grid seats — same
 * elements, same photos, no cross-fade. That continuity is the whole trick.
 */
type Phase = "dark" | "present" | "hold" | "fall" | "exit";

type Seat = { left: number; top: number; width: number; height: number };

/** InfinityHearts play ~ draw + breath + split + fly. */
const SIGN_MS = 2400;
const SIGN_REDUCED_MS = 320;
/** One card, a beat, the next. Slower than the old 520 deal on purpose. */
const PRESENT_STAGGER_MS = 700;
const PRESENT_LEAD_MS = 260;
const SCORE_MS = 520;
const HOLD_AFTER_MS = 460;
const FALL_MS = 520;
const FALL_STAGGER_MS = 45;
const EXIT_MS = 380;

/** Cards are 5:7. */
const CARD_RATIO = 7 / 5;
const SHOWCASE_GUTTER = 8;
const SHOWCASE_GAP = 8;
/** Room kept clear at the bottom for the title + hint. */
const TITLE_RESERVE = 58;

function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/**
 * Grid seats — where the cards land when they fall back to the menu view.
 * Measured against the PACK ROOT, not the stage: the cards are absolutely
 * positioned inside `.match-pack__fan`, so the pack's own box is their
 * coordinate origin. This is what lets the pack go full-viewport without the
 * fall landing in the wrong place.
 */
function measureSeats(root: HTMLElement | null, stage: HTMLElement | null): Seat[] {
  if (!root || !stage) return [];
  const wraps = stage.querySelectorAll<HTMLElement>(
    ".match-week-tile.match-week-tile--fifa"
  );
  if (!wraps.length) return [];
  const base = root.getBoundingClientRect();
  return Array.from(wraps).map((el) => {
    const r = el.getBoundingClientRect();
    return {
      left: r.left - base.left,
      top: r.top - base.top,
      width: r.width,
      height: r.height,
    };
  });
}

/**
 * Where a card waits before it is presented: **off the screen, on the side of
 * its own corner** (Masco 2026-09-20 — one card into the top-left, then one
 * into the top-right, and so on). It arrives at full size and slides into its
 * quadrant, rather than growing out of a stack in the middle.
 */
function entrySeats(root: HTMLElement | null, seats: Seat[]): Seat[] {
  const w = root?.clientWidth || 360;
  return seats.map((s, i) => {
    const fromLeft = i % 2 === 0;
    return {
      ...s,
      left: fromLeft ? -s.width - 48 : w + 48,
      top: s.top + (i < 2 ? -26 : 26),
    };
  });
}

/**
 * Showcase seats: a 2 × 2 that fills about 90 % of the pack, aspect held at
 * 5 : 7, never overflowing. Computed, never hard-coded — orientation and
 * keyboard changes re-run it.
 */
function showcaseSeats(root: HTMLElement | null, n: number): Seat[] {
  const w = root?.clientWidth || 360;
  const h = root?.clientHeight || 640;
  const cols = n <= 1 ? 1 : 2;
  const rows = Math.max(1, Math.ceil(n / cols));
  const availW = w - SHOWCASE_GUTTER * 2 - SHOWCASE_GAP * (cols - 1);
  const availH =
    h - SHOWCASE_GUTTER * 2 - SHOWCASE_GAP * (rows - 1) - TITLE_RESERVE;
  const byWidth = availW / cols;
  const byHeight = availH / rows / CARD_RATIO;
  const cardW = Math.max(96, Math.min(byWidth, byHeight));
  const cardH = cardW * CARD_RATIO;
  const blockW = cardW * cols + SHOWCASE_GAP * (cols - 1);
  const blockH = cardH * rows + SHOWCASE_GAP * (rows - 1);
  const left0 = (w - blockW) / 2;
  const top0 = Math.max(SHOWCASE_GUTTER, (h - TITLE_RESERVE - blockH) / 2);
  return Array.from({ length: n }, (_, i) => ({
    left: left0 + (i % cols) * (cardW + SHOWCASE_GAP),
    top: top0 + Math.floor(i / cols) * (cardH + SHOWCASE_GAP),
    width: cardW,
    height: cardH,
  }));
}

export function PackReveal({
  profiles,
  self,
  viewerPlan,
  mode = "deal",
  onSettle,
  onComplete,
}: {
  profiles: MatchProfile[];
  self: MatchProfile;
  viewerPlan: ProfileTier;
  mode?: "deal" | "refresh";
  onSettle?: () => void;
  onComplete: () => void;
}) {
  const t = useT();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const done = useRef(false);
  const settled = useRef(false);
  const timers = useRef<number[]>([]);
  const rafs = useRef<number[]>([]);
  const refresh = mode === "refresh";
  const four = useMemo(() => profiles.slice(0, 4), [profiles]);
  const scoreKey = four.map((p) => p.id).join("|");
  const scores = useMemo(
    () => four.map((p) => pairAlignmentScore(self, p)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scoreKey, self]
  );

  const [phase, setPhase] = useState<Phase>("dark");
  const [gridSeats, setGridSeats] = useState<Seat[]>([]);
  const [showcase, setShowcase] = useState<Seat[]>(() =>
    showcaseSeats(null, four.length)
  );
  const [deck, setDeck] = useState<Seat[]>(() =>
    entrySeats(null, showcaseSeats(null, four.length))
  );
  const [shown, setShown] = useState<number[]>(() => scores.map(() => 0));
  /** How many cards have been presented (0…4). */
  const [presented, setPresented] = useState(0);
  const [reduced] = useState(prefersReducedMotion);
  const [signPlay, setSignPlay] = useState(true);

  const clearClocks = () => {
    for (const id of timers.current) window.clearTimeout(id);
    for (const id of rafs.current) window.cancelAnimationFrame(id);
    timers.current = [];
    rafs.current = [];
  };

  /**
   * The pack renders through a portal on `document.body`, so the grid it has
   * to land on is found by selector rather than by walking up from the node.
   * Portalling is what lets the reveal cover the header and the dock: both sit
   * in a stacking context the pack could not out-rank from inside the stage,
   * and a reveal with the HUD painted on top of it is not a takeover.
   */
  const stageOf = () =>
    document.querySelector<HTMLElement>(".match-week-stage") ?? null;

  const refreshLayout = () => {
    const root = rootRef.current;
    const show = showcaseSeats(root, four.length);
    setShowcase(show);
    setDeck(entrySeats(root, show));
    const next = measureSeats(root, stageOf());
    if (next.length) setGridSeats(next);
  };

  const finish = () => {
    if (done.current) return;
    done.current = true;
    clearClocks();
    onComplete();
  };

  const animateScore = (index: number, target: number) => {
    const start = performance.now();
    const tick = (now: number) => {
      if (done.current) return;
      const p = Math.min(1, (now - start) / SCORE_MS);
      setShown((prev) => {
        const next = prev.slice();
        next[index] = Math.round(target * p);
        return next;
      });
      if (p < 1) rafs.current.push(window.requestAnimationFrame(tick));
      else {
        setShown((prev) => {
          const next = prev.slice();
          next[index] = target;
          return next;
        });
      }
    };
    rafs.current.push(window.requestAnimationFrame(tick));
  };

  /** All four on the table, scores final, waiting for the press. No timer. */
  const goHold = () => {
    if (done.current) return;
    clearClocks();
    refreshLayout();
    setSignPlay(false);
    setPresented(four.length);
    setShown(scores);
    setPhase("hold");
  };

  /** The press. Cards shrink into the grid seats, then the veil lifts. */
  const goFall = () => {
    if (done.current) return;
    clearClocks();
    refreshLayout();
    setPresented(four.length);
    setShown(scores);
    setPhase("fall");
    if (!settled.current) {
      settled.current = true;
      onSettle?.();
    }
    const travel = reduced
      ? 120
      : FALL_MS + FALL_STAGGER_MS * Math.max(0, four.length - 1);
    timers.current.push(
      window.setTimeout(() => {
        setPhase("exit");
        timers.current.push(window.setTimeout(finish, reduced ? 160 : EXIT_MS));
      }, travel)
    );
  };

  /** Tap / Enter / Space / Escape. Before the hold it skips ahead to it. */
  const onPress = () => {
    if (done.current) return;
    if (phase === "exit") {
      finish();
      return;
    }
    if (phase === "fall") return;
    if (phase === "hold") {
      goFall();
      return;
    }
    goHold();
  };

  useEffect(() => {
    done.current = false;
    settled.current = false;
    setPhase("dark");
    setSignPlay(true);
    setPresented(0);
    setShown(scores.map(() => 0));
    refreshLayout();
    rafs.current.push(window.requestAnimationFrame(() => refreshLayout()));

    const signHold = reduced ? SIGN_REDUCED_MS : SIGN_MS;
    const n = four.length;

    if (reduced) {
      timers.current.push(window.setTimeout(goHold, signHold));
      return () => clearClocks();
    }

    timers.current.push(
      window.setTimeout(() => {
        setSignPlay(false);
        refreshLayout();
        setPhase("present");
        setPresented(0);
      }, signHold)
    );

    const first = signHold + PRESENT_LEAD_MS;
    for (let i = 0; i < n; i += 1) {
      timers.current.push(
        window.setTimeout(() => {
          if (done.current) return;
          refreshLayout();
          setPresented(i + 1);
          animateScore(i, scores[i] ?? 0);
        }, first + i * PRESENT_STAGGER_MS)
      );
    }

    // Enter hold once the last card has settled. Hold itself never expires.
    timers.current.push(
      window.setTimeout(
        () => setPhase((p) => (p === "present" ? "hold" : p)),
        first + (n - 1) * PRESENT_STAGGER_MS + HOLD_AFTER_MS
      )
    );

    return () => clearClocks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, reduced, scoreKey]);

  useEffect(() => {
    const onResize = () => refreshLayout();
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [four.length]);

  const showSign = phase === "dark" && signPlay;
  const fallen = phase === "fall" || phase === "exit";

  const seatFor = (i: number): Seat => {
    const fallback =
      showcase[i] ?? deck[i] ?? showcaseSeats(null, four.length)[i];
    if (fallen) return gridSeats[i] ?? fallback;
    if (phase === "hold") return showcase[i] ?? fallback;
    if (phase === "present") {
      return i < presented ? showcase[i] ?? fallback : deck[i] ?? fallback;
    }
    return deck[i] ?? fallback;
  };

  const body =
    typeof document === "undefined" ? null : document.body;

  const node = (
    <div
      ref={rootRef}
      className={[
        "match-pack",
        refresh ? "match-pack--refresh" : "",
        `is-${phase}`,
        reduced ? "is-reduced" : "",
        showSign ? "is-signing" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      data-viewer-plan={viewerPlan}
      data-presented={presented}
      data-phase={phase}
      role="dialog"
      aria-modal="true"
      aria-labelledby="match-pack-title"
      onClick={onPress}
      onKeyDown={(e) => {
        if (
          e.key === "Escape" ||
          e.key === "Enter" ||
          e.key === " " ||
          e.key === "Spacebar"
        ) {
          e.preventDefault();
          onPress();
        }
      }}
      tabIndex={0}
    >
      <div className="match-pack__veil" aria-hidden />
      {showSign ? (
        <div className="match-pack__sign" aria-hidden>
          <InfinityHearts play className="match-pack__sign-mark" />
        </div>
      ) : null}
      <ul className="match-pack__fan" aria-hidden>
        {four.map((p, i) => {
          const cardTier = tierOf(p);
          const onTable = fallen || phase === "hold" || i < presented;
          const seat = seatFor(i);
          const style = {
            "--i": i,
            left: `${seat.left}px`,
            top: `${seat.top}px`,
            width: `${seat.width}px`,
            height: `${seat.height}px`,
            zIndex: 20 + i,
            transitionDelay: fallen ? `${i * FALL_STAGGER_MS}ms` : "0ms",
          } as CSSProperties;
          return (
            <li
              key={p.id}
              className={`match-pack__card${onTable ? " is-seated" : ""}`}
              style={style}
              data-slot={i}
              data-plan={cardTier}
              data-score-band={scoreBand(scores[i] ?? 0) ?? undefined}
            >
              <span className="match-pack__card-foil" aria-hidden />
              <span className="match-pack__card-photo">
                <span className="match-pack__card-mono" aria-hidden>
                  {p.firstName.charAt(0)}
                </span>
                {p.photoUrl ? (
                  <img
                    className="match-pack__card-img"
                    src={p.photoUrl}
                    alt=""
                    decoding="async"
                    draggable={false}
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                ) : null}
              </span>
              <span className="match-pack__card-name" aria-hidden>
                {p.firstName}
              </span>
              <span
                className="match-pack__card-score"
                style={
                  {
                    "--score-a": scoreInk(shown[i] ?? 0).a,
                    "--score-b": scoreInk(shown[i] ?? 0).b,
                  } as CSSProperties
                }
              >
                {shown[i] ?? 0}
                <em>/100</em>
              </span>
            </li>
          );
        })}
      </ul>
      <div className="match-pack__copy">
        <p id="match-pack-title" className="match-pack__title">
          {refresh ? t("match.newSelection") : t("match.packTitle")}
        </p>
        <p className="match-pack__hint">{t("match.packSkip")}</p>
      </div>
    </div>
  );

  return body ? createPortal(node, body) : node;
}
